'use client';

import { useTagging } from '@/contexts/agent-tag-tool';
import { AskHumanWaiting } from './ask-human-msg';
import { DataSaverMsg } from './data-saver-msg';
import NoConcurrentMsg from './no-concurrent-msg';
import NoPointsMsg from './no-points-msg';
import NotCompletedTaggingMsg from './not-completed-tagging-msg';
import ThreadStatusMsg from './status-message/thread-status-message';
import { NEED_CONTINUE_TAG_TASK_STATUS } from '@/constants/agent';
import { useI18n } from '@/hooks/use-i18n';
import { useAsyncTaskTool } from '@/contexts/async-task-tool-context';
import { NEED_CONTINUE_TASK_STATUS } from '@/constants/async-task-tool';

/**
 * 会话中断消息
 */

interface ThreadInterruptMessageProps {
  status: THREAD.ThreadStatus;
  statusInfo: THREAD.ThreadStatusInfo;
  handleSubmitMessage: THREAD.SendMessageFn;
  // 中止相关状态
  isStopping?: boolean;
}

const ThreadInterruptMessage: React.FC<ThreadInterruptMessageProps> = ({
  status,
  statusInfo,
  handleSubmitMessage,
  isStopping = false,
}) => {
  const { t } = useI18n('thread');
  const { retryTagging, lastTagTask } = useTagging();
  const {
    retry: retryAsyncTask,
    lastAsyncTask,
    isEditResultCompleted,
  } = useAsyncTaskTool();

  // 继续执行异步任务
  const continueAsyncTask = () => {
    const { task_id } = statusInfo || {};
    if (!task_id) return;
    retryAsyncTask(task_id);
  };

  // 继续执行打标任务
  const continueTagTask = () => {
    const { task_id } = statusInfo || {};
    if (!task_id) return;
    retryTagging(task_id);
  };

  const onContinue = async () => {
    if (!statusInfo?.query) {
      return;
    }
    // 构造消息数组
    const message = JSON.stringify([
      {
        type: 'text',
        data: statusInfo?.query,
      },
    ]);
    // 发送
    await handleSubmitMessage([{ content: message }]);
  };

  if (status === 'STOPPED') {
    return (
      <ThreadStatusMsg
        status="stop"
        text={isStopping ? t('message.threadInterrupt.stopping') : undefined}
        className="mb-4"
      />
    );
  }

  if (status === 'GRAPH_RECURSION_LIMIT') {
    return <ThreadStatusMsg status="graph_recursion_limit" className="mb-4" />;
  }

  if (status === 'NO_POINT') {
    return <NoPointsMsg onContinue={onContinue} />;
  }

  if (status === 'DEVICE_NOT_CONNECTED' || status === 'PLATFORM_NOT_LOGIN') {
    return (
      <DataSaverMsg
        errorType={status}
        onContinue={onContinue}
        platformName={statusInfo?.platform_name}
        platform={statusInfo?.platform}
      />
    );
  }
  // 打标异常/中止
  if (
    status === 'ASYNC_TAG_TASK' &&
    NEED_CONTINUE_TAG_TASK_STATUS.includes(lastTagTask?.state)
  ) {
    return <NotCompletedTaggingMsg onContinue={continueTagTask} />;
  }
  // 异步任务异常/中止
  // edit_result 类型的任务：用户点击继续执行后（async_task_completed=true），isEditResultCompleted 为 true，此时不再展示
  if (
    status === 'ASYNC_TASK' &&
    NEED_CONTINUE_TASK_STATUS.includes(lastAsyncTask?.state) &&
    !isEditResultCompleted
  ) {
    return <NotCompletedTaggingMsg onContinue={continueAsyncTask} />;
  }
  // 并发数不足
  if (status === 'OVERLOAD_THREAD') {
    return <NoConcurrentMsg onContinue={onContinue} />;
  }
  if (status === 'ASK_HUMAN') {
    return <AskHumanWaiting />;
  }

  return null;
};

export default ThreadInterruptMessage;
